/**
 * YouTube's Studio numbers, on the YouTube sheet of the dashboard's channel folio: the
 * creator's own channel, read through the YouTube Analytics API with their Google connection.
 * Views, watch time and subscribers by day; who watches (age, gender, country, subscribed or
 * not); where the views come from (traffic sources, YouTube searches, devices, formats); the
 * top videos.
 *
 * It reads its own data and handles every way it can't — no YouTube connection, a connection
 * made without the analytics permission (every connection from before 2026-09-24), the API off
 * on Bearri's side, YouTube's rate limit. It lies ON a sheet, so its parts are ruled blocks,
 * never cards of their own.
 *
 * YouTube's API policy, kept here: every figure is one YouTube returned. A unit may change
 * (minutes → hours) but nothing is computed on top — no net subscribers, no share of a total;
 * age and gender are YouTube's own percentages. Only the creator sees this page.
 */
import React, { useEffect, useMemo, useRef, useState } from 'react';
import { useAuth } from '@clerk/clerk-react';
import { Area, AreaChart, CartesianGrid, Line, LineChart, ReferenceLine, ResponsiveContainer, Tooltip, XAxis, YAxis } from 'recharts';

import {
  ANALYTICS_PERIODS, getYouTubeAnalytics, getYouTubeVideoDetail, getYouTubeVideoStarts, startYouTubeReach,
  type AgeRow, type AnalyticsPeriod, type AnalyticsSectionName, type AnalyticsTotals, type DailyRow,
  type ReachStatus, type RetentionPoint, type TopVideoRow, type VideoDetailResponse, type VideoStart,
  type VideoStarts as VideoStartsReport, type VideoStartsResponse,
  type YouTubeAnalytics as StudioReport, type YouTubeAnalyticsResponse,
} from '../../services/youtubeAnalyticsService';
import { getYouTubeOAuthUrl } from '../../services/youtubeService';
import { FallbackImg, Skeleton, compact, relDate } from './sections';
import { PlatformCta, PrivacyTag, WhatYouGet } from './PlatformCta';
import './youtubeAnalytics.css';

/** OAuthCallback sends a YouTube connect started here back to the YouTube sheet. */
export const OAUTH_RETURN_KEY = 'bearri_oauth_return';
const RETURN_PATH = '/app/dashboard#youtube-analytics';

// SVG attributes need literal colours: these are desk.css's --blue, --paper, --rule, --ink-3
// and --rule-strong. --blue is the one series colour (see youtubeAnalytics.css for why).
const SERIES = '#3a6ea5';
const SURFACE = '#fefdfa';
const GRID = '#d4d0ca';
const AXIS_INK = '#6b7280';
const CROSSHAIR = '#b3aea4';
const UPLOAD_INK = '#254a7c'; // --ink-blue, faint: an event on the chart, not a series

// ── formatting: units only, never a new number ──────────────────────────────────────────
const exact = (n: number) => n.toLocaleString('en-US');
const hoursOf = (minutes: number) => minutes / 60;
const hoursShort = (h: number) => (h >= 1000 ? compact(Math.round(h)) : h >= 10 ? exact(Math.round(h)) : h.toFixed(1));
const hoursExact = (h: number) => (h >= 100 ? exact(Math.round(h)) : h.toFixed(1));
const percent = (n: number) => `${n.toFixed(1)}%`;
/** YouTube's click-through ratio (0.045) as the percentage it stands for (4.5%) — a unit change. */
const ctrText = (r: number | null | undefined) => (r == null ? '—' : `${(r * 100).toFixed(1)}%`);
function duration(seconds: number): string {
  const s = Math.round(seconds);
  const h = Math.floor(s / 3600), m = Math.floor((s % 3600) / 60), sec = s % 60;
  const pad = (v: number) => String(v).padStart(2, '0');
  return h ? `${h}:${pad(m)}:${pad(sec)}` : `${m}:${pad(sec)}`;
}
const orDash = <T,>(v: T | null | undefined, f: (x: T) => string) => (v == null ? '—' : f(v));

// YouTube's dates are calendar days, not instants: read and print them in UTC so a day
// never shifts by the viewer's timezone.
const day = (iso: string, opts: Intl.DateTimeFormatOptions) =>
  new Date(`${iso.slice(0, 10)}T00:00:00Z`).toLocaleDateString('en-US', { timeZone: 'UTC', ...opts });
const shortDay = (iso: string) => day(iso, { month: 'short', day: 'numeric' });
const longDay = (iso: string) => day(iso, { weekday: 'short', month: 'short', day: 'numeric', year: 'numeric' });
function range(start: string, end: string): string {
  const sameYear = start.slice(0, 4) === end.slice(0, 4);
  return `${day(start, sameYear ? { month: 'short', day: 'numeric' } : { month: 'short', day: 'numeric', year: 'numeric' })} – ${day(end, { month: 'short', day: 'numeric', year: 'numeric' })}`;
}

// ── YouTube's names for things, in Studio's words ───────────────────────────────────────
const TRAFFIC_SOURCE: Record<string, string> = {
  ADVERTISING: 'YouTube advertising', ANNOTATION: 'Cards and annotations', CAMPAIGN_CARD: 'Campaign cards',
  END_SCREEN: 'End screens', EXT_URL: 'External', HASHTAGS: 'Hashtag pages', LIVE_REDIRECT: 'Live redirects',
  NO_LINK_EMBEDDED: 'Embedded on other sites', NO_LINK_OTHER: 'Direct or unknown', NOTIFICATION: 'Notifications',
  PLAYLIST: 'Playlists', PRODUCT_PAGE: 'Product pages', PROMOTED: 'YouTube promotions', RELATED_VIDEO: 'Suggested videos',
  SHORTS: 'Shorts feed', SHORTS_CONTENT_LINKS: 'Links in Shorts', SOUND_PAGE: 'Sound pages', SUBSCRIBER: 'Browse features',
  VIDEO_REMIXES: 'Remixed videos', YT_CHANNEL: 'Channel pages', YT_OTHER_PAGE: 'Other YouTube features',
  YT_PLAYLIST_PAGE: 'Playlist pages', YT_SEARCH: 'YouTube search',
};
const DEVICE: Record<string, string> = {
  DESKTOP: 'Computer', MOBILE: 'Mobile phone', TABLET: 'Tablet', TV: 'TV', GAME_CONSOLE: 'Game console', UNKNOWN_PLATFORM: 'Unknown',
};
const CONTENT_TYPE: Record<string, string> = {
  VIDEO_ON_DEMAND: 'Videos', SHORTS: 'Shorts', LIVE_STREAM: 'Live streams', STORY: 'Stories', UNSPECIFIED: 'Other',
};
const SUBSCRIBED: Record<string, string> = { SUBSCRIBED: 'Subscribed', UNSUBSCRIBED: 'Not subscribed' };
const GENDER: Record<string, string> = { female: 'Female', male: 'Male', user_specified: 'User-specified' };
/** An enum YouTube adds later still reads as words: NEW_THING → "New thing". */
const words = (s: string) => (s ? s.charAt(0).toUpperCase() + s.slice(1).toLowerCase().replace(/_/g, ' ') : '—');
const ageGroup = (g: string) => g.replace(/^age/, '').replace(/-$/, '+').replace('-', '–');
const regionNames = (() => {
  try { return new Intl.DisplayNames(['en'], { type: 'region' }); } catch { return null; }
})();
function countryName(code: string): string {
  if (!code || code === 'ZZ') return 'Unknown';
  try { return regionNames?.of(code) || code; } catch { return code; }
}

// ── the four daily series: each a total on its tab and a line on the chart ──────────────
type SeriesKey = 'views' | 'estimatedMinutesWatched' | 'subscribersGained' | 'subscribersLost';
interface Series {
  key: SeriesKey;
  label: string;
  /** API value → plotted value (only minutes → hours changes) */
  plot: (n: number) => number;
  short: (n: number) => string;
  exact: (n: number) => string;
}
const SERIES_LIST: Series[] = [
  { key: 'views', label: 'Views', plot: n => n, short: compact, exact },
  { key: 'estimatedMinutesWatched', label: 'Watch time (hours)', plot: hoursOf, short: m => hoursShort(hoursOf(m)), exact: m => hoursExact(hoursOf(m)) },
  { key: 'subscribersGained', label: 'Subscribers gained', plot: n => n, short: compact, exact },
  { key: 'subscribersLost', label: 'Subscribers lost', plot: n => n, short: compact, exact },
];

// ═════════════════════════════════════════════════════════════════════════════════════════
/** Where the numbers come from. Live, the default export: the signed-in creator's own channel.
 *  The dev desk preview hands in fixtures instead. */
export interface AnalyticsSource {
  /** false until the session is known: the block waits */
  ready: boolean;
  signedIn: boolean;
  read: (days: AnalyticsPeriod, refresh: boolean) => Promise<YouTubeAnalyticsResponse>;
  /** the latest public uploads, each with its own first week */
  readStarts: (refresh: boolean) => Promise<VideoStartsResponse>;
  /** one of the creator's own videos up close */
  readVideo: (video: string, refresh: boolean) => Promise<VideoDetailResponse>;
  /** the creator's click: ask YouTube to prepare thumbnail-reach reports */
  startReach: () => Promise<{ status: string; message?: string }>;
  /** send the creator to Google to (re)connect YouTube; throws with a readable reason */
  connect: () => Promise<void>;
}

/** Which of the creator's videos Bearri found sponsors in — Bearri's own checks of the uploads,
 *  not YouTube data: video id → brand names, and whether the video has been checked at all. */
export type SponsorIndex = Record<string, { brands: string[]; checked: boolean }>;

const YouTubeAnalytics: React.FC<{ sponsors?: SponsorIndex }> = ({ sponsors }) => {
  const { getToken, isLoaded, isSignedIn } = useAuth();
  const source = useMemo<AnalyticsSource>(() => ({
    ready: isLoaded,
    signedIn: Boolean(isSignedIn),
    read: async (days, refresh) => {
      const token = await getToken();
      if (!token) throw new Error('no token');
      return getYouTubeAnalytics(token, days, refresh);
    },
    readStarts: async refresh => {
      const token = await getToken();
      if (!token) throw new Error('no token');
      return getYouTubeVideoStarts(token, refresh);
    },
    readVideo: async (video, refresh) => {
      const token = await getToken();
      if (!token) throw new Error('no token');
      return getYouTubeVideoDetail(token, video, refresh);
    },
    startReach: async () => {
      const token = await getToken();
      if (!token) throw new Error('Sign in again to turn these on.');
      return startYouTubeReach(token);
    },
    connect: async () => {
      const token = await getToken();
      if (!token) throw new Error('Sign in again to connect YouTube.');
      const url = await getYouTubeOAuthUrl(`${window.location.origin}/oauth-callback`, token);
      if (!url) throw new Error('No Google sign-in link came back.');
      try { sessionStorage.setItem(OAUTH_RETURN_KEY, RETURN_PATH); } catch { /* storage off: the callback lands on the profile */ }
      window.location.assign(url);
    },
  }), [getToken, isLoaded, isSignedIn]);
  return <YouTubeAnalyticsView source={source} sponsors={sponsors} />;
};
export default YouTubeAnalytics;

export const YouTubeAnalyticsView: React.FC<{ source: AnalyticsSource; sponsors?: SponsorIndex }> = ({ source, sponsors }) => {
  const { ready, signedIn } = source;
  const [days, setDays] = useState<AnalyticsPeriod>(28);
  const [reloadKey, setReloadKey] = useState(0);
  const skipCache = useRef(false);
  const [data, setData] = useState<YouTubeAnalyticsResponse | null>(null);
  const [busy, setBusy] = useState(true);
  const [failed, setFailed] = useState(false);
  const [connecting, setConnecting] = useState(false);
  const [connectError, setConnectError] = useState<string | null>(null);

  useEffect(() => {
    // Runs on mount, a period change or a click (Try again / Refresh) — never on its own
    // result: a failure sets `failed`, which this effect does not watch.
    if (!ready || !signedIn) return;
    let cancelled = false;
    const refresh = skipCache.current;
    skipCache.current = false;
    setBusy(true);
    (async () => {
      try {
        const result = await source.read(days, refresh);
        if (cancelled) return;
        setData(result);
        setFailed(false);
      } catch {
        if (!cancelled) setFailed(true);
      } finally {
        if (!cancelled) setBusy(false);
      }
    })();
    return () => { cancelled = true; };
  }, [source, ready, signedIn, days, reloadKey]);

  const retry = () => setReloadKey(k => k + 1);
  const [startsRefresh, setStartsRefresh] = useState(0);
  const readAgain = () => { skipCache.current = true; setReloadKey(k => k + 1); setStartsRefresh(n => n + 1); };

  const connect = async () => {
    setConnecting(true);
    setConnectError(null);
    try {
      await source.connect();
    } catch (e) {
      setConnectError(e instanceof Error ? e.message : 'Please try again.');
      setConnecting(false);
    }
  };

  const ok = data?.status === 'ok' ? data : null;
  const starts = useVideoStarts(source.readStarts, startsRefresh, Boolean(ok));
  return (
    <div id="youtube-analytics" className="br-yt scroll-mt-40">
      <div className="br-yt-head">
        <div className="min-w-0">
          <h3 className="br-h3">Studio numbers<PrivacyTag kind="private" /></h3>
          <p className="br-caption">{ok ? `${range(ok.period.start_date, ok.period.end_date)} · from your YouTube Studio`
            : 'Read from your YouTube Studio.'}</p>
        </div>
        {ok && (
          <div className="br-yt-head__tools">
            <div role="group" aria-label="Period" className="flex flex-wrap justify-end gap-1.5">
              {ANALYTICS_PERIODS.map(p => (
                <button key={p} type="button" className="br-tag br-tag--sm" aria-pressed={days === p}
                  onClick={() => setDays(p)}>{p} days</button>
              ))}
            </div>
            <span className="br-caption">
              {busy ? 'Reading YouTube…' : <>Read from YouTube {relDate(ok.fetched_at)} · <button type="button" onClick={readAgain} className="br-inkline">Refresh</button></>}
            </span>
          </div>
        )}
      </div>

      {ready && !signedIn ? (
        <Pencilled text="Sign in to see your Studio numbers." />
      ) : !data ? (
        failed
          ? <Pencilled text="Your YouTube analytics didn't load." action={{ label: 'Try again', onClick: retry }} />
          : <StudioSkeleton />
      ) : data.status === 'ok' ? (
        <>
          {failed && !busy && (
            <p className="br-caption mb-2">These couldn't be refreshed just now; they are the last numbers read.{' '}
              <button type="button" onClick={retry} className="br-inkline">Try again</button></p>
          )}
          <div aria-busy={busy} className={busy ? 'br-yt-holding' : undefined}>
            <StudioBody data={data} starts={starts} sponsors={sponsors} readVideo={source.readVideo} startReach={source.startReach}
              refreshSignal={startsRefresh} />
          </div>
        </>
      ) : (
        <Blocked status={data.status} message={data.message} onRetry={retry}
          onConnect={connect} connecting={connecting} connectError={connectError} />
      )}
    </div>
  );
};

// ── every way there is nothing to show, and what to do about it ─────────────────────────
const Pencilled: React.FC<{
  text: string; note?: string | null; action?: { label: string; onClick: () => void; disabled?: boolean };
}> = ({ text, note, action }) => (
  <div className="br-ruled-empty">
    <p>{text}</p>
    {note && <p className="br-caption mt-1">{note}</p>}
    {action && (
      <button type="button" onClick={action.onClick} disabled={action.disabled} className="br-slab br-slab--blue br-slab--sm">
        {action.label}
      </button>
    )}
  </div>
);

const Blocked: React.FC<{
  status: Exclude<YouTubeAnalyticsResponse['status'], 'ok'>; message?: string;
  onRetry: () => void; onConnect: () => void; connecting: boolean; connectError: string | null;
}> = ({ status, message, onRetry, onConnect, connecting, connectError }) => {
  const connectNote = connectError ? `Couldn't start the YouTube connection: ${connectError}` : null;
  switch (status) {
    case 'not_connected':
    case 'needs_reconnect':
      return (
        <div className="br-yt-connect">
          {status === 'not_connected' && <WhatYouGet id="youtube" heading="Connecting YouTube adds" only="private" />}
          <PlatformCta id="youtube" label={status === 'not_connected' ? 'Connect YouTube' : 'Reconnect YouTube'}
            busy={connecting} busyLabel="Opening Google…" onClick={onConnect} />
          <p className="br-caption">
            {status === 'needs_reconnect'
              ? (message || 'When Google asks, allow “View YouTube Analytics reports for your YouTube content”.')
              : 'Only you will see these numbers. They never appear on your public profile.'}
          </p>
          {connectNote && <p className="br-caption" role="alert">{connectNote}</p>}
        </div>
      );
    case 'api_not_enabled':
      return <Pencilled text="YouTube analytics is switched off on Bearri's side right now. That's ours to fix, not yours; your YouTube connection is fine." />;
    case 'rate_limited':
      return <Pencilled text="YouTube asked Bearri to slow down. Try again in a few minutes." action={{ label: 'Try again', onClick: onRetry }} />;
    default:
      return <Pencilled text="YouTube analytics didn't load this time." note={message} action={{ label: 'Try again', onClick: onRetry }} />;
  }
};

const StudioSkeleton: React.FC = () => (
  <div>
    <p className="sr-only">Loading your YouTube analytics…</p>
    <div className="mb-4 grid grid-cols-2 gap-4 md:grid-cols-4">
      {[0, 1, 2, 3].map(i => <div key={i} className="space-y-2"><Skeleton className="h-2.5 w-20" /><Skeleton className="h-7 w-16" /></div>)}
    </div>
    <Skeleton className="h-[232px] w-full" />
  </div>
);

/** A report YouTube didn't return: one plain line, with YouTube's own reason on hover. */
const NotReturned: React.FC<{ reason: string }> = ({ reason }) => (
  <p className="br-caption" title={reason}>YouTube didn't return this for your channel.</p>
);

// ── the numbers ─────────────────────────────────────────────────────────────────────────
/** A section's rows, or why YouTube didn't give any. */
function rowsOf<K extends AnalyticsSectionName>(data: StudioReport, name: K) {
  return { rows: data.sections[name] ?? null, unavailable: data.unavailable?.[name] ?? null };
}

const StudioBody: React.FC<{
  data: StudioReport; starts: StartsState; sponsors?: SponsorIndex;
  readVideo: AnalyticsSource['readVideo']; startReach: AnalyticsSource['startReach']; refreshSignal: number;
}> = ({ data, starts, sponsors, readVideo, startReach, refreshSignal }) => {
  const totals: AnalyticsTotals = data.sections.totals?.[0] ?? {};
  const age = rowsOf(data, 'age_groups');
  const gender = rowsOf(data, 'genders');
  const countries = rowsOf(data, 'countries');
  const subscribed = rowsOf(data, 'subscribed_status');
  const sources = rowsOf(data, 'traffic_sources');
  const searches = rowsOf(data, 'search_terms');
  const devices = rowsOf(data, 'devices');
  const formats = rowsOf(data, 'content_types');
  const startsOk = starts.data?.status === 'ok' ? starts.data : null;
  const viewerNote = 'Share of signed-in viewers, as YouTube reports it.';
  const hiddenNote = 'YouTube shows this only once enough signed-in viewers have watched.';

  return (
    <div className="br-yt-blocks">
      <div className="br-yt-block">
        <DailyOverview totals={totals} daily={data.sections.daily ?? []} unavailable={data.unavailable?.daily ?? null}
          period={data.period} reach={startsOk?.reach} previous={data.sections.previous_totals?.[0] ?? null}
          uploads={(startsOk?.videos ?? []).map(v => ({ day: v.published_at.slice(0, 10), title: v.title || v.video }))} />
      </div>

      <div className="br-yt-block"><PitchCard data={data} starts={startsOk} sponsors={sponsors} /></div>

      <div className="br-yt-block"><VideoStartsBlock state={starts} sponsors={sponsors} startReach={startReach} /></div>

      {startsOk && startsOk.videos.length > 0 && (
        <div className="br-yt-block">
          <VideoCloseUp videos={startsOk.videos} windowDays={startsOk.window_days} read={readVideo} refreshSignal={refreshSignal} />
        </div>
      )}

      <div className="br-yt-block">
        <h4 className="br-yt-block__title">Audience</h4>
        <div className="grid grid-cols-1 gap-x-8 gap-y-6 md:grid-cols-2">
          <BarList title="Age" unavailable={age.unavailable} note={viewerNote} empty={hiddenNote}
            items={age.rows?.map(r => ({ key: r.ageGroup, label: ageGroup(r.ageGroup), value: r.viewerPercentage, display: percent(r.viewerPercentage) }))} />
          <BarList title="Gender" unavailable={gender.unavailable} note={viewerNote} empty={hiddenNote}
            items={gender.rows?.map(r => ({ key: r.gender, label: GENDER[r.gender] || words(r.gender), value: r.viewerPercentage, display: percent(r.viewerPercentage) }))} />
          <BarList title="Top countries · views" unavailable={countries.unavailable}
            items={countries.rows?.map(r => ({ key: r.country, label: countryName(r.country), value: r.views, display: exact(r.views) }))} />
          <BarList title="Subscribed or not · views" unavailable={subscribed.unavailable}
            items={subscribed.rows?.map(r => ({ key: r.subscribedStatus, label: SUBSCRIBED[r.subscribedStatus] || words(r.subscribedStatus), value: r.views, display: exact(r.views) }))} />
        </div>
      </div>

      <div className="br-yt-block">
        <h4 className="br-yt-block__title">Where views come from</h4>
        <div className="grid grid-cols-1 gap-x-8 gap-y-6 md:grid-cols-2">
          <BarList title="Traffic sources · views" unavailable={sources.unavailable}
            items={sources.rows?.map(r => ({ key: r.insightTrafficSourceType, label: TRAFFIC_SOURCE[r.insightTrafficSourceType] || words(r.insightTrafficSourceType), value: r.views, display: exact(r.views) }))} />
          <BarList title="YouTube searches · views" unavailable={searches.unavailable}
            empty="No YouTube searches led to your videos in this period."
            items={searches.rows?.map(r => ({ key: r.insightTrafficSourceDetail, label: r.insightTrafficSourceDetail, value: r.views, display: exact(r.views) }))} />
          <BarList title="Devices · views" unavailable={devices.unavailable}
            items={devices.rows?.map(r => ({ key: r.deviceType, label: DEVICE[r.deviceType] || words(r.deviceType), value: r.views, display: exact(r.views) }))} />
          <BarList title="Formats · views" unavailable={formats.unavailable}
            items={formats.rows?.map(r => ({ key: r.creatorContentType, label: CONTENT_TYPE[r.creatorContentType] || words(r.creatorContentType), value: r.views, display: exact(r.views) }))} />
        </div>
      </div>

      <div className="br-yt-block"><TopVideosBlock data={data} /></div>
    </div>
  );
};

// ── the daily chart, headed by its four totals ─────────────────────────────────────────
const DailyOverview: React.FC<{
  totals: AnalyticsTotals; daily: DailyRow[]; unavailable: string | null; period: StudioReport['period']; reach?: ReachStatus;
  /** the same totals for the period just before — shown beside, never as a % change */
  previous: AnalyticsTotals | null;
  /** the latest uploads, marked on the chart where they fall */
  uploads: { day: string; title: string }[];
}> = ({ totals, daily, unavailable, period, reach, previous, uploads }) => {
  const reach28 = reach?.status === 'ok' ? reach.last_28 : null;
  const [seriesKey, setSeriesKey] = useState<SeriesKey>('views');
  const series = SERIES_LIST.find(s => s.key === seriesKey) ?? SERIES_LIST[0];
  // A day YouTube returned without this number stays a gap — never a 0 it did not report.
  const newOn = new Map<string, string[]>();
  for (const u of uploads) newOn.set(u.day, [...(newOn.get(u.day) ?? []), u.title]);
  const points = daily.map(r => ({ day: r.day, value: r[series.key] == null ? null : series.plot(r[series.key] as number), uploaded: newOn.get(r.day) ?? [] }));
  const marked = points.filter(p => p.uploaded.length).map(p => p.day);
  const lastDay = daily.length ? daily[daily.length - 1].day : null;
  const yearLong = period.days > 90;

  return (
    <>
      <div role="group" aria-label="Chart shows" className="br-yt-tabs">
        {SERIES_LIST.map(s => (
          <button key={s.key} type="button" className="br-yt-tab" aria-pressed={s.key === seriesKey} onClick={() => setSeriesKey(s.key)}>
            <span className="br-figure__legend">{s.label}</span>
            <span className="br-figure__value">{orDash(totals[s.key], s.short)}</span>
            {previous && typeof previous[s.key] === 'number' && (
              <span className="br-yt-tab__before">{s.short(previous[s.key] as number)} the {period.days} days before</span>
            )}
          </button>
        ))}
      </div>

      {unavailable ? <NotReturned reason={unavailable} />
        : !daily.length ? <p className="br-caption">No daily numbers for this period yet.</p>
        : (
          <figure className="m-0" aria-label={`${series.label} by day`}>
            <div className="h-[232px] w-full">
              <ResponsiveContainer width="100%" height="100%">
                <AreaChart data={points} margin={{ top: 8, right: 8, bottom: 0, left: 0 }} accessibilityLayer>
                  <CartesianGrid vertical={false} stroke={GRID} />
                  <XAxis dataKey="day" tickLine={false} axisLine={{ stroke: GRID }} minTickGap={28}
                    tick={{ fontSize: 11, fill: AXIS_INK }}
                    tickFormatter={(d: string) => (yearLong ? day(d, { month: 'short', year: 'numeric' }) : shortDay(d))} />
                  <YAxis width={48} tickLine={false} axisLine={false} allowDecimals={series.key === 'estimatedMinutesWatched'}
                    tick={{ fontSize: 11, fill: AXIS_INK }} tickFormatter={(v: number) => compact(v)} />
                  {marked.map(d => <ReferenceLine key={d} x={d} stroke={UPLOAD_INK} strokeOpacity={0.4} strokeWidth={1} ifOverflow="hidden" />)}
                  <Tooltip cursor={{ stroke: CROSSHAIR, strokeWidth: 1 }} isAnimationActive={false}
                    content={<DayTip series={series} />} />
                  <Area type="monotone" dataKey="value" stroke={SERIES} strokeWidth={2} fill={SERIES} fillOpacity={0.1}
                    dot={false} activeDot={{ r: 4, fill: SERIES, stroke: SURFACE, strokeWidth: 2 }} isAnimationActive={false} />
                </AreaChart>
              </ResponsiveContainer>
            </div>
            <figcaption className="br-caption mt-1">
              {lastDay && lastDay < period.end_date ? `Daily numbers through ${shortDay(lastDay)}. YouTube adds the latest days after a short delay. ` : ''}
              {marked.length > 0 ? 'Thin lines mark your latest uploads; hover a day to see which.' : ''}
            </figcaption>
          </figure>
        )}

      <dl className="br-yt-stats">
        <div><dt>Average view duration</dt><dd>{orDash(totals.averageViewDuration, duration)}</dd></div>
        <div><dt>Average percentage viewed</dt><dd>{orDash(totals.averageViewPercentage, percent)}</dd></div>
        <div><dt>Likes</dt><dd>{orDash(totals.likes, compact)}</dd></div>
        <div><dt>Comments</dt><dd>{orDash(totals.comments, compact)}</dd></div>
        <div><dt>Shares</dt><dd>{orDash(totals.shares, compact)}</dd></div>
        {reach28 && <>
          <div><dt>Thumbnail impressions · last 28 days</dt><dd>{compact(reach28.video_thumbnail_impressions)}</dd></div>
          <div><dt>Impressions click-through · last 28 days</dt><dd>{ctrText(reach28.video_thumbnail_impressions_ctr)}</dd></div>
        </>}
      </dl>

      {daily.length > 0 && !unavailable && (
        <details className="br-yt-days">
          <summary>See the numbers by day</summary>
          <div className="br-yt-days__scroll">
            <table className="br-yt-table br-yt-table--days">
              <thead><tr><th scope="col">Day</th>{SERIES_LIST.map(s => <th key={s.key} scope="col">{s.label}</th>)}</tr></thead>
              <tbody>
                {[...daily].reverse().map(r => (
                  <tr key={r.day}>
                    <td>{longDay(r.day)}</td>
                    {SERIES_LIST.map(s => <td key={s.key}>{orDash(r[s.key], s.exact)}</td>)}
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </details>
      )}
    </>
  );
};

/** The chart's hover card (recharts injects active/payload/label). */
const DayTip: React.FC<{ series: Series; active?: boolean; payload?: { value?: number | null; payload?: { uploaded?: string[] } }[]; label?: string }> = ({
  series, active, payload, label,
}) => {
  const v = payload?.[0]?.value;
  const uploaded = payload?.[0]?.payload?.uploaded ?? [];
  if (!active || v == null || !label) return null;
  // the plotted value is already in display units (hours for watch time)
  const shown = series.key === 'estimatedMinutesWatched' ? hoursExact(v) : exact(v);
  return (
    <div className="br-yt-tip">
      <div className="br-yt-tip__value"><span className="br-yt-tip__key" aria-hidden="true" />{shown}</div>
      <div>{series.label}</div>
      <div>{longDay(label)}</div>
      {uploaded.map(t => <div key={t} className="br-yt-tip__new">New: {t}</div>)}
    </div>
  );
};

// ── a ranked list: label, a thin bar, the value written at its tip ──────────────────────
interface BarItem { key: string; label: string; value: number; display: string }
const BarList: React.FC<{
  title: string; items?: BarItem[] | null; unavailable?: string | null; note?: string; empty?: string;
}> = ({ title, items, unavailable, note, empty = 'Nothing for this period.' }) => {
  const max = Math.max(0, ...(items || []).map(i => i.value));
  return (
    <div>
      <h4 className="br-yt-list__title">{title}</h4>
      {unavailable ? <NotReturned reason={unavailable} />
        : !items?.length ? <p className="br-caption">{empty}</p>
        : (
          <ol className="br-yt-bars">
            {items.map(item => (
              <li key={item.key} className="br-yt-bar">
                <span className="br-yt-bar__label" title={item.label}>{item.label}</span>
                <span className="br-yt-bar__track" aria-hidden="true">
                  {item.value > 0 && max > 0 && <span className="br-yt-bar__fill" style={{ width: `${Math.max(1.5, (item.value / max) * 100)}%` }} />}
                </span>
                <span className="br-yt-bar__value">{item.display}</span>
              </li>
            ))}
          </ol>
        )}
      {note && items && items.length > 0 && !unavailable && <p className="br-caption mt-2">{note}</p>}
    </div>
  );
};

// ── the top videos, as YouTube ranks them ───────────────────────────────────────────────
const TopVideos: React.FC<{ videos: TopVideoRow[]; by?: 'views' | 'subscribers' }> = ({ videos, by = 'views' }) => {
  const cols: { label: string; cell: (v: TopVideoRow) => string }[] = [
    { label: 'Views', cell: v => exact(v.views) },
    { label: 'Watch time (hours)', cell: v => orDash(v.estimatedMinutesWatched, m => hoursExact(hoursOf(m))) },
    { label: 'Average view duration', cell: v => orDash(v.averageViewDuration, duration) },
    { label: 'Average percentage viewed', cell: v => orDash(v.averageViewPercentage, percent) },
    { label: 'Subscribers gained', cell: v => orDash(v.subscribersGained, exact) },
  ];
  // ranked by subscribers, that column comes first and the averages YouTube didn't rank on stay out
  const shown = by === 'subscribers' ? [cols[4], cols[0], cols[1]] : cols;
  return (
    <div className="overflow-x-auto">
      <table className="br-yt-table">
        <thead>
          <tr><th scope="col">Video</th>{shown.map(c => <th key={c.label} scope="col">{c.label}</th>)}</tr>
        </thead>
        <tbody>
          {videos.map(v => (
            <tr key={v.video}>
              <td>
                <div className="br-yt-video">
                  <FallbackImg src={v.thumbnail_url} imgClassName="br-yt-video__thumb"
                    fallback={<span className="br-yt-video__thumb" aria-hidden="true" />} />
                  <div className="min-w-0">
                    <a className="br-yt-video__title" href={`https://www.youtube.com/watch?v=${encodeURIComponent(v.video)}`}
                      target="_blank" rel="noopener noreferrer">{v.title || v.video}</a>
                    {v.published_at && <div className="br-caption">Published {day(v.published_at, { month: 'short', day: 'numeric', year: 'numeric' })}</div>}
                  </div>
                </div>
              </td>
              {shown.map(c => <td key={c.label}>{c.cell(v)}</td>)}
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
};

const TopVideosBlock: React.FC<{ data: StudioReport }> = ({ data }) => {
  const [by, setBy] = useState<'views' | 'subscribers'>('views');
  const list = rowsOf(data, by === 'views' ? 'top_videos' : 'top_by_subscribers');
  return (
    <>
      <div className="br-yt-block__row">
        <h4 className="br-yt-block__title">Top videos</h4>
        <div role="group" aria-label="Rank by" className="flex flex-wrap gap-1.5">
          <button type="button" className="br-tag br-tag--sm" aria-pressed={by === 'views'} onClick={() => setBy('views')}>Most viewed</button>
          <button type="button" className="br-tag br-tag--sm" aria-pressed={by === 'subscribers'} onClick={() => setBy('subscribers')}>Most subscribers</button>
        </div>
      </div>
      <p className="br-caption mb-3">{by === 'views' ? 'Your most-viewed videos in this period.' : 'The videos that brought you the most subscribers in this period, as YouTube ranks them.'}</p>
      {list.unavailable ? <NotReturned reason={list.unavailable} />
        : !list.rows?.length ? <p className="br-caption">{by === 'views' ? 'No video views in this period.' : 'No new subscribers came from a video in this period.'}</p>
        : <TopVideos videos={list.rows} by={by} />}
    </>
  );
};

// ── how the latest uploads started: each one's first week, side by side ──────────────────
type StartsState = { data: VideoStartsResponse | null; failed: boolean; retry: () => void };

/** The latest uploads' first weeks, read once the main numbers are in (so a creator without
 *  YouTube connected triggers nothing). Runs on mount, Refresh or Try again — never on its own
 *  result. */
function useVideoStarts(read: AnalyticsSource['readStarts'], refreshSignal: number, enabled: boolean): StartsState {
  const [data, setData] = useState<VideoStartsResponse | null>(null);
  const [failed, setFailed] = useState(false);
  const [retryKey, setRetryKey] = useState(0);
  const lastSignal = useRef(refreshSignal);
  useEffect(() => {
    if (!enabled) return;
    let cancelled = false;
    const refresh = refreshSignal !== lastSignal.current;
    lastSignal.current = refreshSignal;
    setFailed(false);
    read(refresh).then(r => { if (!cancelled) setData(r); }).catch(() => { if (!cancelled) setFailed(true); });
    return () => { cancelled = true; };
  }, [read, refreshSignal, retryKey, enabled]);
  return { data, failed, retry: () => setRetryKey(k => k + 1) };
}

/** YouTube's format for a video, as it names it; UNSPECIFIED says nothing, so it's left unsaid. */
const formatOf = (v: VideoStart) => (v.creatorContentType && v.creatorContentType !== 'UNSPECIFIED' ? v.creatorContentType : null);
const FORMAT_ONE: Record<string, string> = { SHORTS: 'Short', VIDEO_ON_DEMAND: 'Video', LIVE_STREAM: 'Live', STORY: 'Story' };
const FORMAT_PLURAL: Record<string, string> = { SHORTS: 'Shorts', VIDEO_ON_DEMAND: 'Videos', LIVE_STREAM: 'Live streams', STORY: 'Stories' };

type SponsorState = 'sponsored' | 'none' | 'unchecked';
const sponsorOf = (sponsors: SponsorIndex | undefined, video: string): SponsorState => {
  const entry = sponsors?.[video];
  return entry?.brands.length ? 'sponsored' : entry?.checked ? 'none' : 'unchecked';
};
const SPONSOR_LABEL: Record<SponsorState, string> = {
  sponsored: 'Sponsored', none: 'No sponsor found', unchecked: 'Not checked for sponsors yet',
};

/** Each of the latest public uploads with YouTube's numbers for its own first 7 days, newest
 *  first, a bar each on one scale — so the ones that took off stand out on sight — coloured by
 *  what Bearri found in it: sponsored (the sponsors' ochre), no sponsor (the desk blue), or not
 *  checked yet (grey). Sponsored next to unsponsored is the comparison brands ask about.
 *  Nothing is computed on top (no average line, no "x times usual") until YouTube's
 *  derived-metrics approval. A newer video's shorter window is a lighter step of its colour. */
const VideoStartsBlock: React.FC<{ state: StartsState; sponsors?: SponsorIndex; startReach: AnalyticsSource['startReach'] }> = ({ state, sponsors, startReach }) => {
  const { data, failed, retry } = state;
  const ok = data?.status === 'ok' ? data : null;
  const all = ok?.videos ?? [];
  const formats = Array.from(new Set(all.map(v => formatOf(v)).filter((f): f is string => !!f)));
  const [format, setFormat] = useState<string | null>(null);
  const showing = format && formats.includes(format) ? format : null;
  const videos = showing ? all.filter(v => formatOf(v) === showing) : all;
  // one scale per view: Shorts and long videos compared among their own kind
  const max = Math.max(0, ...videos.map(v => v.views ?? 0));
  const windowDays = ok?.window_days ?? 7;
  const present = (['sponsored', 'none', 'unchecked'] as SponsorState[]).filter(st => videos.some(v => sponsorOf(sponsors, v.video) === st));
  // with no video checked for sponsors yet, there is nothing to tell apart: plain desk-blue bars
  const sponsorAware = present.some(st => st !== 'unchecked');
  return (
    <>
      <h4 className="br-yt-block__title">How your latest videos started</h4>
      <p className="br-caption mb-3">Views in each video's first {windowDays} days, as YouTube reports them. Newest first. Sponsors are from Bearri's checks of your videos.</p>
      {failed || (data && !ok) ? (
        <p className="br-caption">YouTube didn't return your latest videos this time.{' '}
          <button type="button" onClick={retry} className="br-inkline">Try again</button></p>
      ) : !data ? (
        <div className="space-y-2" aria-busy="true">{[0, 1, 2].map(i => <Skeleton key={i} className="h-9 w-full" />)}</div>
      ) : !all.length ? (
        <p className="br-caption">No public videos yet.</p>
      ) : (
        <>
          <ReachNote reach={ok?.reach} startReach={startReach} onStarted={retry} />
          {formats.length > 1 && (
            <div role="group" aria-label="Show" className="mb-3 flex flex-wrap gap-1.5">
              <button type="button" className="br-tag br-tag--sm" aria-pressed={!showing} onClick={() => setFormat(null)}>All</button>
              {formats.map(f => (
                <button key={f} type="button" className="br-tag br-tag--sm" aria-pressed={showing === f} onClick={() => setFormat(f)}>{FORMAT_PLURAL[f] ?? words(f)}</button>
              ))}
            </div>
          )}
          {sponsorAware && present.length > 1 && (
            <ul className="br-yt-legend" aria-label="Bar colours">
              {present.map(st => <li key={st} className="br-yt-legend__item" data-sponsor={st}><span className="br-yt-legend__swatch" aria-hidden="true" />{SPONSOR_LABEL[st]}</li>)}
            </ul>
          )}
          <ol className="br-yt-starts">
            {videos.map(v => <VideoStartRow key={v.video} v={v} max={max} windowDays={windowDays}
              sponsor={sponsorAware ? sponsorOf(sponsors, v.video) : null} brands={sponsors?.[v.video]?.brands ?? []} />)}
          </ol>
        </>
      )}
    </>
  );
};

const VideoStartRow: React.FC<{ v: VideoStart; max: number; windowDays: number; sponsor: SponsorState | null; brands: string[] }> = ({
  v, max, windowDays, sponsor, brands,
}) => {
  const partial = v.window.days < windowDays;
  const format = formatOf(v);
  const when = `${format ? `${FORMAT_ONE[format] ?? words(format)} · ` : ''}${day(v.published_at, { month: 'short', day: 'numeric' })} · ${partial ? `${v.window.days} ${v.window.days === 1 ? 'day' : 'days'} so far` : `first ${windowDays} days`}`;
  return (
    <li className="br-yt-start" data-partial={partial || undefined} data-sponsor={sponsor ?? undefined}>
      <FallbackImg src={v.thumbnail_url} imgClassName="br-yt-video__thumb" fallback={<span className="br-yt-video__thumb" aria-hidden="true" />} />
      <div className="br-yt-start__info">
        <a className="br-yt-video__title" href={`https://www.youtube.com/watch?v=${encodeURIComponent(v.video)}`}
          target="_blank" rel="noopener noreferrer">{v.title || v.video}</a>
        <span className="br-caption">
          {when}{typeof v.averageViewPercentage === 'number' ? ` · ${percent(v.averageViewPercentage)} viewed` : ''}
        </span>
        {v.reach && (
          <span className="br-caption">
            {compact(v.reach.video_thumbnail_impressions)} thumbnail impressions · {ctrText(v.reach.video_thumbnail_impressions_ctr)} click-through
            {v.reach.days < v.window.days ? ` (${v.reach.days} of ${v.window.days} days reported)` : ''}
          </span>
        )}
        {sponsor === 'sponsored' && <span className="br-yt-start__sponsor">Sponsored · {brands.join(', ')}</span>}
      </div>
      <span className="br-yt-bar__track br-yt-start__track" aria-hidden="true">
        {!!v.views && max > 0 && <span className="br-yt-bar__fill" style={{ width: `${Math.max(1.5, (v.views / max) * 100)}%` }} />}
      </span>
      <span className="br-yt-start__value" title={v.unavailable || undefined}>
        {v.unavailable ? '—' : typeof v.views === 'number' ? exact(v.views) : 'No data yet'}
      </span>
    </li>
  );
};

// ── what to tell a brand: the numbers sponsors ask for, ready to paste ───────────────────
/** The numbers a sponsor asks for, each one YouTube's own (for the period chosen above), and the
 *  latest sponsored video's first week — written as lines a creator can paste into a pitch.
 *  Picking the largest age group or the first countries is reading YouTube's ranking, not a new
 *  number. Copying is the creator's own act: nothing is shared by Bearri. */
const PitchCard: React.FC<{ data: StudioReport; starts: VideoStartsReport | null; sponsors?: SponsorIndex }> = ({ data, starts, sponsors }) => {
  const [copied, setCopied] = useState<'idle' | 'done' | 'failed'>('idle');
  const timer = useRef<number | null>(null);
  useEffect(() => () => { if (timer.current) window.clearTimeout(timer.current); }, []);

  const t = data.sections.totals?.[0] ?? {};
  const lines: string[] = [];
  if (typeof t.views === 'number') lines.push(`${exact(t.views)} views in the last ${data.period.days} days`);
  if (typeof t.averageViewDuration === 'number') {
    lines.push(`${duration(t.averageViewDuration)} average view duration${typeof t.averageViewPercentage === 'number' ? ` (${percent(t.averageViewPercentage)} of each video watched)` : ''}`);
  }
  if (typeof t.subscribersGained === 'number') lines.push(`${exact(t.subscribersGained)} new subscribers`);
  const genders = (data.sections.genders ?? []).filter(g => g.gender !== 'user_specified')
    .map(g => `${percent(g.viewerPercentage)} ${(GENDER[g.gender] || words(g.gender)).toLowerCase()}`);
  const topAge = (data.sections.age_groups ?? []).reduce<AgeRow | null>((top, r) => (!top || r.viewerPercentage > top.viewerPercentage ? r : top), null);
  const audience = [genders.join(', '), topAge ? `largest age group ${ageGroup(topAge.ageGroup)} (${percent(topAge.viewerPercentage)})` : ''].filter(Boolean);
  if (audience.length) lines.push(`Audience: ${audience.join('; ')}`);
  const countries = (data.sections.countries ?? []).map(c => countryName(c.country)).filter(n => n !== 'Unknown').slice(0, 3);
  if (countries.length) lines.push(`Top countries by views: ${countries.join(', ')}`);
  const sponsored = (starts?.videos ?? []).find(v => sponsorOf(sponsors, v.video) === 'sponsored'
    && typeof v.views === 'number' && v.window.days >= (starts?.window_days ?? 7));
  if (sponsored) lines.push(`Latest sponsored video (${sponsors![sponsored.video].brands.join(', ')}): ${exact(sponsored.views!)} views in its first ${sponsored.window.days} days`);
  const fullWeeks = (starts?.videos ?? []).filter(v => typeof v.views === 'number' && v.window.days >= (starts?.window_days ?? 7)).slice(0, 5);
  if (fullWeeks.length >= 2) lines.push(`First-week views of my latest videos: ${fullWeeks.map(v => exact(v.views!)).join(' · ')}`);
  if (!lines.length) return null;

  const text = [`My YouTube channel, ${range(data.period.start_date, data.period.end_date)}:`, ...lines.map(l => `• ${l}`), 'Numbers from YouTube Analytics.'].join('\n');
  const copy = async () => {
    try {
      await navigator.clipboard.writeText(text);
      setCopied('done');
    } catch {
      setCopied('failed');
    }
    if (timer.current) window.clearTimeout(timer.current);
    timer.current = window.setTimeout(() => setCopied('idle'), 2500);
  };
  return (
    <>
      <div className="br-yt-block__row">
        <h4 className="br-yt-block__title">What to tell a brand</h4>
        <button type="button" onClick={copy} className="br-slab br-slab--blue br-slab--sm">
          {copied === 'done' ? 'Copied' : 'Copy for a pitch'}
        </button>
      </div>
      <p className="br-caption mb-3">The numbers sponsors ask for, straight from YouTube for the period above. Copy them into a pitch.</p>
      <ul className="br-yt-pitch">{lines.map(l => <li key={l}>{l}</li>)}</ul>
      {copied === 'failed' && <p className="br-caption mt-2" role="alert">Your browser didn't allow copying. Select the lines and copy them.</p>}
      <span className="sr-only" aria-live="polite">{copied === 'done' ? 'Copied to your clipboard.' : ''}</span>
    </>
  );
};

// ── where viewers leave a video ──────────────────────────────────────────────────────────
const clock = (seconds: number) => duration(Math.max(0, seconds));
const RELATIVE_TICK: Record<number, string> = { 0: 'Lower', 0.5: 'Typical', 1: 'Higher' };

/** One of the latest videos up close (the newest with a full first week, unless another is
 *  picked), everything YouTube reports for it over its life so far: its totals, its thumbnail
 *  reach, where viewers leave — the retention curve and, as its own chart, never a second axis,
 *  YouTube's comparison with every video of similar length — where its views came from, and the
 *  YouTube searches that found it. All YouTube's numbers. */
const VideoCloseUp: React.FC<{ videos: VideoStart[]; windowDays: number; read: AnalyticsSource['readVideo']; refreshSignal: number }> = ({
  videos, windowDays, read, refreshSignal,
}) => {
  const fallback = (videos.find(v => v.window.days >= windowDays) ?? videos[0]).video;
  const [chosen, setChosen] = useState<string | null>(null);
  const current = chosen && videos.some(v => v.video === chosen) ? chosen : fallback;
  const cache = useRef<Record<string, VideoDetailResponse>>({});
  const [shown, setShown] = useState<{ video: string; res: VideoDetailResponse | null; failed: boolean } | null>(null);
  const [retryKey, setRetryKey] = useState(0);
  const lastSignal = useRef(refreshSignal);

  useEffect(() => {
    // Runs when the video, Refresh or Try again changes — never on its own result.
    let cancelled = false;
    const refresh = refreshSignal !== lastSignal.current;
    lastSignal.current = refreshSignal;
    if (refresh) cache.current = {};
    const hit = cache.current[current];
    if (hit) { setShown({ video: current, res: hit, failed: false }); return; }
    setShown({ video: current, res: null, failed: false });
    read(current, refresh)
      .then(res => { if (cancelled) return; cache.current[current] = res; setShown({ video: current, res, failed: false }); })
      .catch(() => { if (!cancelled) setShown({ video: current, res: null, failed: true }); });
    return () => { cancelled = true; };
  }, [current, read, refreshSignal, retryKey]);

  const res = shown?.video === current ? shown.res : null;
  const ok = res?.status === 'ok' ? res : null;
  const seconds = ok?.video.duration_seconds ?? null;
  const at = (p: RetentionPoint) => (seconds ? p.elapsedVideoTimeRatio * seconds : p.elapsedVideoTimeRatio * 100);
  const tick = (v: number) => (seconds ? clock(v) : `${Math.round(v)}%`);
  const points = (ok?.points ?? []).map(p => ({
    t: at(p),
    watching: p.audienceWatchRatio == null ? null : p.audienceWatchRatio * 100,
    relative: p.relativeRetentionPerformance ?? null,
    ratio: p.elapsedVideoTimeRatio,
  }));
  const tenths = points.filter(p => Math.round(p.ratio * 100) % 10 === 0);
  const t: AnalyticsTotals = ok?.sections?.totals?.[0] ?? {};
  const reach = ok?.reach?.status === 'ok' ? ok.reach : null;
  const sources = ok?.sections?.traffic_sources;
  const searches = ok?.sections?.search_terms;
  const start = videos.find(v => v.video === current);
  return (
    <>
      <div className="br-yt-block__row">
        <h4 className="br-yt-block__title">One video, up close</h4>
        <label className="br-yt-pick">
          <span className="br-caption">Video</span>
          <select value={current} onChange={e => setChosen(e.target.value)} className="br-yt-select">
            {videos.map(v => <option key={v.video} value={v.video}>{v.title || v.video}</option>)}
          </select>
        </label>
      </div>
      <p className="br-caption mb-3">Everything YouTube reports for one video, over its life so far.</p>
      {shown?.failed || (res && !ok) ? (
        <p className="br-caption">YouTube didn't return this video.{' '}
          <button type="button" onClick={() => setRetryKey(k => k + 1)} className="br-inkline">Try again</button></p>
      ) : !res || !ok ? (
        <Skeleton className="h-[200px] w-full" />
      ) : (
        <>
          <div className="br-yt-closeup__video">
            <FallbackImg src={ok.video.thumbnail_url} imgClassName="br-yt-video__thumb" fallback={<span className="br-yt-video__thumb" aria-hidden="true" />} />
            <div className="min-w-0">
              <a className="br-yt-video__title" href={`https://www.youtube.com/watch?v=${encodeURIComponent(ok.video.video)}`}
                target="_blank" rel="noopener noreferrer">{ok.video.title || ok.video.video}</a>
              <span className="br-caption">
                {ok.video.published_at ? `Published ${day(ok.video.published_at, { month: 'short', day: 'numeric', year: 'numeric' })}` : ''}
                {seconds ? ` · ${clock(seconds)} long` : ''}
              </span>
            </div>
          </div>
          {ok.unavailable?.totals ? <NotReturned reason={ok.unavailable.totals} /> : (
            <dl className="br-yt-stats br-yt-stats--4">
              <div><dt>Views</dt><dd>{orDash(t.views, exact)}</dd></div>
              <div><dt>Watch time (hours)</dt><dd>{orDash(t.estimatedMinutesWatched, m => hoursExact(hoursOf(m)))}</dd></div>
              <div><dt>Average view duration</dt><dd>{orDash(t.averageViewDuration, duration)}</dd></div>
              <div><dt>Average percentage viewed</dt><dd>{orDash(t.averageViewPercentage, percent)}</dd></div>
              <div><dt>Likes</dt><dd>{orDash(t.likes, exact)}</dd></div>
              <div><dt>Comments</dt><dd>{orDash(t.comments, exact)}</dd></div>
              <div><dt>Shares</dt><dd>{orDash(t.shares, exact)}</dd></div>
              <div><dt>Subscribers gained</dt><dd>{orDash(t.subscribersGained, exact)}</dd></div>
            </dl>
          )}
          {((start && typeof start.views === 'number') || reach) && (
            <dl className="br-yt-stats">
              {start && typeof start.views === 'number' && (
                <div><dt>{start.window.days < windowDays ? `Views, ${start.window.days} days so far` : `Views, first ${windowDays} days`}</dt><dd>{exact(start.views)}</dd></div>
              )}
              {reach?.first_week && <>
                <div><dt>Thumbnail impressions, first {windowDays} days</dt><dd>{compact(reach.first_week.video_thumbnail_impressions)}</dd></div>
                <div><dt>Click-through, first {windowDays} days</dt><dd>{ctrText(reach.first_week.video_thumbnail_impressions_ctr)}</dd></div>
              </>}
              {reach?.last_28 && <>
                <div><dt>Thumbnail impressions, last 28 days</dt><dd>{compact(reach.last_28.video_thumbnail_impressions)}</dd></div>
                <div><dt>Click-through, last 28 days</dt><dd>{ctrText(reach.last_28.video_thumbnail_impressions_ctr)}</dd></div>
              </>}
            </dl>
          )}

          <h5 className="br-yt-sub">Where viewers leave</h5>
          {!points.length ? (
            <p className="br-caption">YouTube needs more views on this video before it can show where viewers leave.</p>
          ) : (
        <div className="grid grid-cols-1 gap-6 lg:grid-cols-[3fr_2fr]">
            <figure className="m-0" aria-label="Viewers still watching">
              <p className="br-yt-list__title">Still watching</p>
              <div className="h-[200px] w-full">
                <ResponsiveContainer width="100%" height="100%">
                  <AreaChart data={points} margin={{ top: 8, right: 8, bottom: 0, left: 0 }} accessibilityLayer>
                    <CartesianGrid vertical={false} stroke={GRID} />
                    <XAxis dataKey="t" type="number" domain={[0, 'dataMax']} tickLine={false} axisLine={{ stroke: GRID }}
                      tick={{ fontSize: 11, fill: AXIS_INK }} tickFormatter={tick} minTickGap={28} />
                    <YAxis width={44} tickLine={false} axisLine={false} tick={{ fontSize: 11, fill: AXIS_INK }}
                      tickFormatter={(v: number) => `${Math.round(v)}%`} domain={[0, 'auto']} />
                    <Tooltip cursor={{ stroke: CROSSHAIR, strokeWidth: 1 }} isAnimationActive={false}
                      content={<PointTip tick={tick} render={p => `${Math.round(p.watching ?? 0)}% still watching`} />} />
                    <Area type="monotone" dataKey="watching" stroke={SERIES} strokeWidth={2} fill={SERIES} fillOpacity={0.1}
                      dot={false} activeDot={{ r: 4, fill: SERIES, stroke: SURFACE, strokeWidth: 2 }} isAnimationActive={false} />
                  </AreaChart>
                </ResponsiveContainer>
              </div>
              <figcaption className="br-caption mt-1">Share of viewers still watching at each moment. Above 100% at the start means people went back and rewatched.</figcaption>
            </figure>
            <figure className="m-0" aria-label="Compared with videos of similar length">
              <p className="br-yt-list__title">Compared with videos of its length</p>
              <div className="h-[200px] w-full">
                <ResponsiveContainer width="100%" height="100%">
                  <LineChart data={points} margin={{ top: 8, right: 8, bottom: 0, left: 0 }} accessibilityLayer>
                    <CartesianGrid vertical={false} stroke={GRID} />
                    <XAxis dataKey="t" type="number" domain={[0, 'dataMax']} tickLine={false} axisLine={{ stroke: GRID }}
                      tick={{ fontSize: 11, fill: AXIS_INK }} tickFormatter={tick} minTickGap={28} />
                    <YAxis width={56} tickLine={false} axisLine={false} domain={[0, 1]} ticks={[0, 0.5, 1]}
                      tick={{ fontSize: 11, fill: AXIS_INK }} tickFormatter={(v: number) => RELATIVE_TICK[v] ?? ''} />
                    <ReferenceLine y={0.5} stroke={CROSSHAIR} strokeWidth={1} />
                    <Tooltip cursor={{ stroke: CROSSHAIR, strokeWidth: 1 }} isAnimationActive={false}
                      content={<PointTip tick={tick} render={p => (p.relative == null ? 'No comparison here'
                        : `${p.relative.toFixed(2)} · ${p.relative > 0.5 ? 'above' : p.relative < 0.5 ? 'below' : 'at'} typical (0.50)`)} />} />
                    <Line type="monotone" dataKey="relative" stroke={SERIES} strokeWidth={2} dot={false}
                      activeDot={{ r: 4, fill: SERIES, stroke: SURFACE, strokeWidth: 2 }} isAnimationActive={false} />
                  </LineChart>
                </ResponsiveContainer>
              </div>
              <figcaption className="br-caption mt-1">YouTube's comparison with every video of similar length. Above the middle line, this video holds viewers better than typical at that moment; below it, worse.</figcaption>
            </figure>
            {tenths.length > 0 && (
              <details className="br-yt-days lg:col-span-2">
                <summary>See the numbers</summary>
                <div className="br-yt-days__scroll">
                  <table className="br-yt-table br-yt-table--days">
                    <thead><tr><th scope="col">Moment</th><th scope="col">Still watching</th><th scope="col">Compared with its length (0.50 typical)</th></tr></thead>
                    <tbody>{tenths.map(p => (
                      <tr key={p.ratio}><td>{tick(p.t)}</td><td>{p.watching == null ? '—' : `${Math.round(p.watching)}%`}</td>
                        <td>{p.relative == null ? '—' : p.relative.toFixed(2)}</td></tr>
                    ))}</tbody>
                  </table>
                </div>
              </details>
            )}
          </div>
          )}

          <div className="mt-6 grid grid-cols-1 gap-x-8 gap-y-6 md:grid-cols-2">
            <BarList title="Where its views came from · views" unavailable={ok.unavailable?.traffic_sources ?? null}
              items={sources?.map(r => ({ key: r.insightTrafficSourceType, label: TRAFFIC_SOURCE[r.insightTrafficSourceType] || words(r.insightTrafficSourceType), value: r.views, display: exact(r.views) }))} />
            <BarList title="YouTube searches that found it · views" unavailable={ok.unavailable?.search_terms ?? null}
              empty="No YouTube searches led to this video yet."
              items={searches?.map(r => ({ key: r.insightTrafficSourceDetail, label: r.insightTrafficSourceDetail, value: r.views, display: exact(r.views) }))} />
          </div>
        </>
      )}
    </>
  );
};

/** Whether YouTube's thumbnail-reach reports are on, and the one click that turns them on: it
 *  starts a reporting job on the creator's channel, so it's asked for, never done silently. */
const ReachNote: React.FC<{ reach?: ReachStatus; startReach: AnalyticsSource['startReach']; onStarted: () => void }> = ({
  reach, startReach, onStarted,
}) => {
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  if (!reach) return null;
  const turnOn = async () => {
    setBusy(true);
    setError(null);
    try {
      const result = await startReach();
      if (result.status !== 'preparing') throw new Error(result.message || 'YouTube said no.');
      onStarted();
    } catch (e) {
      setError(e instanceof Error ? e.message : 'Please try again.');
    } finally {
      setBusy(false);
    }
  };
  if (reach.status === 'off') {
    return (
      <div className="br-yt-reach">
        <p className="br-small">See how often YouTube shows each thumbnail and how many people click it. YouTube prepares
          these as daily reports, starting with the 30 days before today; the first numbers arrive within 2 days.</p>
        <button type="button" onClick={turnOn} disabled={busy} className="br-slab br-slab--white br-slab--sm">
          {busy ? 'Turning on…' : 'Turn on click-through reports'}
        </button>
        {error && <p className="br-caption" role="alert">Couldn't turn them on: {error}</p>}
      </div>
    );
  }
  if (reach.status === 'preparing') {
    return <p className="br-caption mb-3">Click-through reports are on{reach.since ? ` (since ${shortDay(reach.since)})` : ''}. YouTube is preparing them; the first numbers usually arrive within 2 days.</p>;
  }
  if (reach.status === 'unavailable') {
    return <p className="br-caption mb-3">Thumbnail numbers didn't load{reach.message ? `: ${reach.message}` : '.'}</p>;
  }
  return <p className="br-caption mb-3">Thumbnail impressions and click-through from YouTube's daily reach reports, {range(reach.coverage.start_date, reach.coverage.end_date)}.</p>;
};

type RetentionDatum = { t: number; watching: number | null; relative: number | null };
/** The retention charts' hover card: the reading first, then the moment. */
const PointTip: React.FC<{ tick: (v: number) => string; render: (p: RetentionDatum) => string; active?: boolean; payload?: { payload?: RetentionDatum }[] }> = ({
  tick, render, active, payload,
}) => {
  const p = payload?.[0]?.payload;
  if (!active || !p) return null;
  return (
    <div className="br-yt-tip">
      <div className="br-yt-tip__value"><span className="br-yt-tip__key" aria-hidden="true" />{render(p)}</div>
      <div>At {tick(p.t)}</div>
    </div>
  );
};
